import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { playSoundEffect } from '../../../hooks/useSoundEffects';
import {
  AUTOBAHN_LANE_SIMON_BACKGROUND_SRC,
  AUTOBAHN_LANE_SIMON_WATCH_SOUND_SRC,
  AUTOBAHN_LANE_SIMON_LANE_PICK_SOUND_SRC,
  AUTOBAHN_LANE_SIMON_LANE_SLOTS,
  buildAutobahnLaneSlotsFromBoundaries,
  AUTOBAHN_LANE_SIMON_WIN_PICK_VALUE,
  AUTOBAHN_LANE_SIMON_LOSS_PICK_VALUE,
  AUTOBAHN_LANE_SIMON_STEP_DISPLAY_MS,
  AUTOBAHN_LANE_SIMON_STEP_GAP_MS,
  AUTOBAHN_LANE_SIMON_LOGICAL_WIDTH,
  AUTOBAHN_LANE_SIMON_LOGICAL_HEIGHT,
  SHOW_AUTOBAHN_LANE_SIMON_DEV_OUTLINES,
  autobahnLaneSimonSlotToSvgPoints,
  autobahnLaneSimonSlotLabelPercentPosition,
} from './autobahnLaneSimonLayout';
import AutobahnLaneBoundaryEditor from './AutobahnLaneBoundaryEditor';
import './AutobahnLaneSimonMinigame.css';

/**
 * @param {object} props
 * @param {{ pattern: number[], patternLength: number, laneCount: number, winPickValue: number, lossPickValue: number, stepDisplayMs?: number, stepGapMs?: number }} props.challenge
 * @param {(pickedTreeIndex: number, opts?: { pickedLaneIndices?: number[] }) => void} props.onPickTree
 * @param {boolean} props.disabled
 * @param {React.RefObject<HTMLElement>} props.containerRef
 * @param {null | 'won' | 'lost'} props.resultOutcome
 * @param {(() => void) | undefined} props.onResultProceed
 * @param {{ id: string, xpGained?: number, darknessReductionApplied?: number, billyBucksAmount?: number, startHealthBonus?: number } | null} props.winReward
 * @param {number[] | undefined} props.laneBoundaryXBottom
 * @param {boolean} props.boundaryEditMode
 * @param {(boundaries: number[]) => void} [props.onBoundaryAuthoringChange]
 */
export default function AutobahnLaneSimonMinigame({
  challenge,
  onPickTree,
  disabled = false,
  containerRef,
  resultOutcome = null,
  onResultProceed,
  winReward = null,
  laneBoundaryXBottom,
  boundaryEditMode = false,
  onBoundaryAuthoringChange,
}) {
  const { t } = useTranslation();
  const playArenaRef = useRef(null);
  const submittedRef = useRef(false);
  const timersRef = useRef([]);

  const [phase, setPhase] = useState('watch');
  const [activeLaneIndex, setActiveLaneIndex] = useState(null);
  const [watchProgress, setWatchProgress] = useState(0);
  const [playerPicks, setPlayerPicks] = useState([]);

  const challengeReady =
    challenge != null &&
    Array.isArray(challenge.pattern) &&
    challenge.pattern.length > 0 &&
    Number.isInteger(challenge.laneCount);

  const pattern = challengeReady ? challenge.pattern : [];
  const patternLength = challengeReady ? challenge.patternLength ?? pattern.length : 0;
  const winPickValue = challengeReady
    ? challenge.winPickValue ?? AUTOBAHN_LANE_SIMON_WIN_PICK_VALUE
    : AUTOBAHN_LANE_SIMON_WIN_PICK_VALUE;
  const lossPickValue = challengeReady
    ? challenge.lossPickValue ?? AUTOBAHN_LANE_SIMON_LOSS_PICK_VALUE
    : AUTOBAHN_LANE_SIMON_LOSS_PICK_VALUE;
  const stepDisplayMs = challengeReady
    ? challenge.stepDisplayMs ?? AUTOBAHN_LANE_SIMON_STEP_DISPLAY_MS
    : AUTOBAHN_LANE_SIMON_STEP_DISPLAY_MS;
  const stepGapMs = challengeReady
    ? challenge.stepGapMs ?? AUTOBAHN_LANE_SIMON_STEP_GAP_MS
    : AUTOBAHN_LANE_SIMON_STEP_GAP_MS;

  const laneSlots = useMemo(() => {
    if (!challengeReady) {
      return [];
    }
    const slots = Array.isArray(laneBoundaryXBottom)
      ? buildAutobahnLaneSlotsFromBoundaries(laneBoundaryXBottom)
      : AUTOBAHN_LANE_SIMON_LANE_SLOTS;
    return slots.filter((slot) => slot.id < challenge.laneCount);
  }, [challengeReady, challenge?.laneCount, laneBoundaryXBottom]);

  const winRewardBodyKey =
    resultOutcome === 'won' && winReward && typeof winReward.id === 'string'
      ? `exploreUnknownPage.minigame.autobahnLaneSimon.winReward.${winReward.id}`
      : null;

  const clearTimers = useCallback(() => {
    for (const id of timersRef.current) {
      window.clearTimeout(id);
    }
    timersRef.current = [];
  }, []);

  const setPlayArenaRef = useCallback(
    (node) => {
      playArenaRef.current = node;
      if (containerRef && typeof containerRef === 'object') {
        containerRef.current = node;
      }
    },
    [containerRef]
  );

  const submitComplete = useCallback(
    (pickedTreeIndex, pickedLaneIndices) => {
      if (submittedRef.current || disabled) return;
      submittedRef.current = true;
      onPickTree(pickedTreeIndex, { pickedLaneIndices });
    },
    [disabled, onPickTree]
  );

  useEffect(() => {
    if (!challengeReady || resultOutcome != null) {
      return undefined;
    }
    if (!Array.isArray(pattern) || pattern.length < 1) {
      throw new Error('AutobahnLaneSimonMinigame: challenge.pattern is required');
    }
    clearTimers();
    setPhase('watch');
    setActiveLaneIndex(null);
    setWatchProgress(0);
    setPlayerPicks([]);
    submittedRef.current = false;

    let step = 0;
    const scheduleNext = () => {
      if (step >= pattern.length) {
        const doneTimer = window.setTimeout(() => {
          setActiveLaneIndex(null);
          setPhase('repeat');
        }, stepGapMs);
        timersRef.current.push(doneTimer);
        return;
      }
      const laneIndex = pattern[step];
      const showTimer = window.setTimeout(() => {
        setActiveLaneIndex(laneIndex);
        setWatchProgress(step + 1);
        playSoundEffect(AUTOBAHN_LANE_SIMON_WATCH_SOUND_SRC);
        step += 1;
        const hideTimer = window.setTimeout(() => {
          setActiveLaneIndex(null);
          scheduleNext();
        }, stepDisplayMs);
        timersRef.current.push(hideTimer);
      }, step === 0 ? stepGapMs : stepGapMs);
      timersRef.current.push(showTimer);
    };
    scheduleNext();

    return () => {
      clearTimers();
    };
  }, [challengeReady, challenge, pattern, clearTimers, resultOutcome, stepDisplayMs, stepGapMs]);

  const handleLaneClick = useCallback(
    (laneIndex, event) => {
      if (phase !== 'repeat' || disabled || submittedRef.current || resultOutcome != null) {
        return;
      }
      if (event?.currentTarget && typeof event.currentTarget.blur === 'function') {
        event.currentTarget.blur();
      }
      if (!Number.isInteger(laneIndex) || laneIndex < 0) {
        throw new Error(`AutobahnLaneSimonMinigame: invalid laneIndex (${String(laneIndex)})`);
      }
      const nextPicks = [...playerPicks, laneIndex];
      setPlayerPicks(nextPicks);
      playSoundEffect(AUTOBAHN_LANE_SIMON_LANE_PICK_SOUND_SRC);
      if (laneIndex !== pattern[nextPicks.length - 1]) {
        submitComplete(lossPickValue, nextPicks);
        return;
      }
      if (nextPicks.length === pattern.length) {
        submitComplete(winPickValue, nextPicks);
      }
    },
    [phase, disabled, resultOutcome, playerPicks, pattern, submitComplete, lossPickValue, winPickValue]
  );

  const promptText =
    resultOutcome === 'won'
      ? winRewardBodyKey
        ? t(winRewardBodyKey, {
            percent: winReward?.darknessReductionApplied,
            xp: winReward?.xpGained,
            billyBucks: winReward?.billyBucksAmount,
            health: winReward?.startHealthBonus != null ? winReward.startHealthBonus : 3,
          })
        : t('exploreUnknownPage.minigame.autobahnLaneSimon.success')
      : resultOutcome === 'lost'
        ? t('exploreUnknownPage.minigame.autobahnLaneSimon.failure')
        : phase === 'watch'
          ? t('exploreUnknownPage.minigame.autobahnLaneSimon.watchPrompt', {
              step: watchProgress,
              total: patternLength,
            })
          : t('exploreUnknownPage.minigame.autobahnLaneSimon.repeatPrompt', {
              step: playerPicks.length + 1,
              total: patternLength,
            });

  if (!challengeReady) {
    return (
      <div
        ref={setPlayArenaRef}
        className="autobahn-lane-simon-play-arena autobahn-lane-simon-play-arena--challenge-error"
        role="alert"
      >
        <p>{t('exploreUnknownPage.minigame.autobahnLaneSimon.missingChallenge')}</p>
      </div>
    );
  }

  const showDevLaneOutlines =
    boundaryEditMode ||
    (process.env.NODE_ENV === 'development' && SHOW_AUTOBAHN_LANE_SIMON_DEV_OUTLINES);

  return (
    <div
      ref={setPlayArenaRef}
      className="autobahn-lane-simon-play-arena"
      role="application"
      aria-label={t('exploreUnknownPage.minigame.autobahnLaneSimon.playArenaAria')}
    >
      <img
        src={AUTOBAHN_LANE_SIMON_BACKGROUND_SRC}
        alt=""
        className="autobahn-lane-simon-bg"
        draggable={false}
      />
      <div className="autobahn-lane-simon-prompt-strip">
        <h2 className="autobahn-lane-simon-title">{t('exploreUnknownPage.minigame.autobahnLaneSimon.title')}</h2>
        <p>{promptText}</p>
      </div>
      <svg
        className="autobahn-lane-simon-lanes-svg"
        viewBox={`0 0 ${AUTOBAHN_LANE_SIMON_LOGICAL_WIDTH} ${AUTOBAHN_LANE_SIMON_LOGICAL_HEIGHT}`}
        preserveAspectRatio="xMidYMid meet"
        aria-hidden={phase === 'repeat'}
      >
        {laneSlots.map((slot) => {
          const isActive = phase === 'watch' && activeLaneIndex === slot.id;
          const isPicked =
            phase === 'repeat' && playerPicks.length > 0 && playerPicks[playerPicks.length - 1] === slot.id;
          const laneInteractive =
            !boundaryEditMode &&
            phase === 'repeat' &&
            !disabled &&
            !submittedRef.current &&
            resultOutcome == null;
          return (
            <polygon
              key={slot.id}
              points={autobahnLaneSimonSlotToSvgPoints(slot)}
              className={[
                'autobahn-lane-simon-lane-polygon',
                isActive ? 'autobahn-lane-simon-lane-polygon--active' : '',
                isPicked ? 'autobahn-lane-simon-lane-polygon--picked' : '',
                showDevLaneOutlines ? 'autobahn-lane-simon-lane-polygon--dev-outline' : '',
                laneInteractive ? 'autobahn-lane-simon-lane-polygon--interactive' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              role="button"
              tabIndex={laneInteractive ? 0 : -1}
              aria-label={t('exploreUnknownPage.minigame.autobahnLaneSimon.laneAria', {
                lane: slot.id + 1,
              })}
              aria-disabled={!laneInteractive}
              onClick={(e) => handleLaneClick(slot.id, e)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleLaneClick(slot.id, e);
                }
              }}
            />
          );
        })}
      </svg>
      <div className="autobahn-lane-simon-lane-labels" aria-hidden>
        {laneSlots.map((slot) => {
          const labelPos = autobahnLaneSimonSlotLabelPercentPosition(slot);
          return (
            <span
              key={`label-${slot.id}`}
              className="autobahn-lane-simon-lane-label"
              style={{
                left: labelPos.left,
                top: labelPos.top,
                transform: 'translate(-50%, -50%)',
              }}
            >
              {t('exploreUnknownPage.minigame.autobahnLaneSimon.laneLabel', { lane: slot.id + 1 })}
            </span>
          );
        })}
      </div>
      {boundaryEditMode &&
      typeof onBoundaryAuthoringChange === 'function' &&
      Array.isArray(laneBoundaryXBottom) ? (
        <AutobahnLaneBoundaryEditor
          containerRef={playArenaRef}
          boundaries={laneBoundaryXBottom}
          onBoundariesChange={onBoundaryAuthoringChange}
        />
      ) : null}
      {resultOutcome != null ? (
        <div
          className={`autobahn-lane-simon-result-banner autobahn-lane-simon-result-banner--${resultOutcome}`}
          role="status"
          aria-live="polite"
        >
          <p className="autobahn-lane-simon-result-banner-title">
            {resultOutcome === 'won'
              ? t('exploreUnknownPage.minigame.autobahnLaneSimon.successTitle')
              : t('exploreUnknownPage.minigame.autobahnLaneSimon.failureTitle')}
          </p>
          <p className="autobahn-lane-simon-result-banner-body">
            {resultOutcome === 'won'
              ? winRewardBodyKey
                ? t(winRewardBodyKey, {
                    percent: winReward?.darknessReductionApplied,
                    xp: winReward?.xpGained,
                    billyBucks: winReward?.billyBucksAmount,
                    health: winReward?.startHealthBonus != null ? winReward.startHealthBonus : 3,
                  })
                : t('exploreUnknownPage.minigame.autobahnLaneSimon.successBodyGeneric')
              : t('exploreUnknownPage.minigame.autobahnLaneSimon.failureBody')}
          </p>
          {typeof onResultProceed === 'function' ? (
            <button
              type="button"
              className="autobahn-lane-simon-result-proceed"
              onClick={() => onResultProceed()}
            >
              {t('exploreUnknownPage.minigame.proceed')}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
